import PropTypes from 'prop-types';

import Checkbox from '#globalComponents/Checkbox/Checkbox';

import {
	ALIGN_CLASS,
	CELL_PADDING,
	columnStyle,
	SELECTION_COLUMN_WIDTH,
	stickyCellBackground,
	stickyEdgeClass,
} from './tableStyles';

const ROW_STATE_CLASS = {
	highlighted: 'bg-primary-100',
	selected: 'bg-[#f6f4ff]',
	expanded: 'bg-[#faf9ff]',
};

const resolveRowState = ({ isSelected, isHighlighted, isExpanded }) => {
	if (isHighlighted) return 'highlighted';
	if (isSelected) return 'selected';
	if (isExpanded) return 'expanded';
	return undefined;
};

const TableRow = ({
	row,
	rowId,
	columns,
	scrolls,
	selection,
	isSelected = false,
	isHighlighted = false,
	isExpanded = false,
	onRowClick,
	rowProps,
}) => {
	const stickyBg = stickyCellBackground({ isSelected, isHighlighted, isExpanded });

	const rowState = resolveRowState({ isSelected, isHighlighted, isExpanded });
	const rowStateClass = ROW_STATE_CLASS[rowState] ?? 'hover:bg-[#fbfaff]';

	return (
		<tr
			{...rowProps}
			// Tinted rows expose their state so cell content can adapt via `group-data-[state]/row:`.
			data-state={rowState}
			className={`group/row border-b border-bgBase-400 transition-colors ${rowStateClass} ${
				onRowClick ? 'cursor-pointer' : ''
			} ${rowProps?.className ?? ''}`}
			onClick={onRowClick ? () => onRowClick(row) : undefined}
			tabIndex={onRowClick ? 0 : undefined}
			onKeyDown={
				onRowClick
					? (e) => {
							if (e.target !== e.currentTarget) return;
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onRowClick(row);
							}
						}
					: undefined
			}
		>
			{selection && (
				<td
					className={`sticky left-0 z-10 ${stickyBg} ${CELL_PADDING} transition-colors`}
					style={{ width: SELECTION_COLUMN_WIDTH }}
					onClick={(e) => e.stopPropagation()}
				>
					<Checkbox
						id={`row-${rowId}`}
						checked={isSelected}
						onChange={() => selection.onToggleOne(rowId)}
						aria-label="Zeile auswählen"
					/>
				</td>
			)}
			{columns.map((column, index) => (
				<td
					key={column.key}
					className={`${CELL_PADDING} transition-colors ${ALIGN_CLASS[column.align] ?? ALIGN_CLASS.left} ${
						column.sticky ? `sticky z-10 ${stickyBg}` : ''
					} ${stickyEdgeClass(column.sticky, scrolls)} ${column.cellClassName ?? ''}`}
					style={{ ...columnStyle(columns, index, Boolean(selection)), ...column.cellStyle }}
				>
					{index === 0 || column.grow ? (
						<div
							className={column.grow ? 'w-0 min-w-full overflow-hidden' : 'overflow-hidden'}
							style={{ width: column.contentWidth, maxWidth: column.contentMaxWidth }}
						>
							{column.render(row)}
						</div>
					) : (
						column.render(row)
					)}
				</td>
			))}
		</tr>
	);
};

TableRow.propTypes = {
	row: PropTypes.object.isRequired,
	rowId: PropTypes.string.isRequired,
	columns: PropTypes.array.isRequired,
	scrolls: PropTypes.bool,
	selection: PropTypes.shape({ onToggleOne: PropTypes.func.isRequired }),
	isSelected: PropTypes.bool,
	isHighlighted: PropTypes.bool,
	isExpanded: PropTypes.bool,
	onRowClick: PropTypes.func,
	rowProps: PropTypes.object,
};

export default TableRow;
